/**
 * How a humanoid is drawn, apart from what it is (proportions) and what it wears (look).
 *
 * `lpc` is the style fitted to sit beside LPC art: its raster, its sampled skins, and the muscle
 * marks painted on the LPC-fitted body. `realist` is Ember's own, for bodies at real human
 * proportions: picked skins with cool shadows and a plum outline, one key light cut into clean
 * steps instead of LPC's rim of shade inside every outline, and no LPC marks (they are calibrated to the
 * LPC body and would land in the wrong places on any other). The styles are content
 * (`content/game/sprites/ember/styles.json`), bound into EMBER_STYLES in place.
 */
import { bindContent } from "../content/catalog"
import { EmberClip, NATURAL_CLIPS } from "./clips"
import { noteEmberContent } from "./model"
import { Material, SKIN_SETS } from "./palette"
import { DEFAULT_RASTER, RasterOptions } from "./raster"

export interface EmberStyle {
    raster: RasterOptions
    skins: Record<string, Material>
    /** The muscle marks it paints (marks.ts MARK_SETS), if any. */
    marks?: string
    /** The eye style (face.ts) for a look that names none. */
    eyes?: string
    /** Clips drawn this style's own way, by clip name, over the shared EMBER_CLIPS. */
    clips?: Record<string, EmberClip>
}

/** The clip sets a style may play its own way (styles.json `clips`), by name. Clips are the engine's. */
export const EMBER_CLIP_SETS: Record<string, Record<string, EmberClip>> = { natural: NATURAL_CLIPS }

/** A style as content writes it: the raster over the engine's, and its skins, marks, eyes and clips by name. */
export interface EmberStyleSource {
    about?: string
    raster?: Record<string, unknown>
    skins: string
    marks?: string
    eyes?: string
    clips?: string
}
export interface EmberStyleFile {
    version: 1
    about?: string
    default: string
    styles: Record<string, EmberStyleSource>
}

export const EMBER_STYLES: Record<string, EmberStyle> = {}
export let DEFAULT_STYLE = "lpc"

const isPlain = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v)
/** `over` merged into `base` object by object; anything else (numbers, lists) replaces. */
const merged = <T>(base: T, over: Record<string, unknown> | undefined): T => {
    if (!over) return { ...base }
    const out: Record<string, unknown> = { ...(base as Record<string, unknown>) }
    for (const [k, v] of Object.entries(over)) out[k] = isPlain(v) && isPlain(out[k]) ? merged(out[k], v) : v
    return out as T
}

/** Fill the styles from content's styles.json (bound below). Skin sets must be bound first (palette.ts). */
export const registerStyles = (file: EmberStyleFile | undefined): void => {
    for (const k of Object.keys(EMBER_STYLES)) delete EMBER_STYLES[k]
    for (const [name, s] of Object.entries(file?.styles ?? {})) {
        EMBER_STYLES[name] = {
            raster: merged(DEFAULT_RASTER, s.raster),
            skins: SKIN_SETS[s.skins] ?? {},
            ...(s.marks ? { marks: s.marks } : {}),
            ...(s.eyes ? { eyes: s.eyes } : {}),
            ...(s.clips ? { clips: EMBER_CLIP_SETS[s.clips] } : {}),
        }
    }
    DEFAULT_STYLE = file?.default ?? "lpc"
    noteEmberContent("styles", file)
}

bindContent(catalog => (catalog.source as any).emberStyles, registerStyles)
